import { useEffect, useRef } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { WebLinksAddon } from "@xterm/addon-web-links";
import type { ClientMessage } from "@shared/protocol";

export interface AgentBinding {
  agentId: string;
  readonly: boolean;
  send: (msg: ClientMessage) => boolean;
  /** Stream of output bytes from server. Caller pushes here via `write`. */
}

/**
 * Attach an xterm.js instance to a container, wired to the given agent.
 * Returns an imperative handle so the parent can call `.write(data)` when
 * agent:output messages arrive on the websocket.
 */
export function useAgentTerminal(
  container: HTMLElement | null,
  binding: AgentBinding | null
) {
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);

  useEffect(() => {
    if (!container || !binding) return;
    const term = new Terminal({
      fontFamily: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
      fontSize: 12,
      lineHeight: 1.4,
      cursorBlink: !binding.readonly,
      cursorStyle: "bar",
      disableStdin: binding.readonly,
      allowProposedApi: true,
      convertEol: true,
      theme: {
        background: "#161618",
        foreground: "#ECEAE4",
        cursor: "#FF3B1F",
        cursorAccent: "#0E0E0F",
        selectionBackground: "#FF3B1F44",
        black: "#1C1C1F",
        red: "#FF5747",
        green: "#4ADE80",
        yellow: "#F5B544",
        blue: "#6AA9FF",
        magenta: "#A78BFA",
        cyan: "#67E8F9",
        white: "#ECEAE4",
        brightBlack: "#3D3A36",
        brightRed: "#FF7A6C",
        brightGreen: "#6FD08C",
        brightYellow: "#F5B544",
        brightBlue: "#6AA9FF",
        brightMagenta: "#A78BFA",
        brightCyan: "#67E8F9",
        brightWhite: "#FFFFFF",
      },
    });

    const fit = new FitAddon();
    const links = new WebLinksAddon();
    term.loadAddon(fit);
    term.loadAddon(links);
    term.open(container);
    fit.fit();

    termRef.current = term;
    fitRef.current = fit;

    const onData = term.onData((data) => {
      if (binding.readonly) return;
      binding.send({ type: "agent:input", payload: { agentId: binding.agentId, data } });
    });
    const onResize = term.onResize(({ cols, rows }) => {
      binding.send({ type: "agent:resize", payload: { agentId: binding.agentId, cols, rows } });
    });

    const ro = new ResizeObserver(() => {
      try { fit.fit(); } catch { /* container hidden */ }
    });
    ro.observe(container);

    return () => {
      ro.disconnect();
      onData.dispose();
      onResize.dispose();
      term.dispose();
      termRef.current = null;
      fitRef.current = null;
    };
  }, [container, binding?.agentId, binding?.readonly]);

  return {
    write: (data: string) => termRef.current?.write(data),
    clear: () => termRef.current?.clear(),
    fit: () => { try { fitRef.current?.fit(); } catch { /* noop */ } },
  };
}
